// shared-lib/dimensioning/pick.js
//
// Picking the DATUM a dimension is taken from.
//
// `centered-between` never needs this: its two boundaries are auto-picked from
// the walls the subject faces. The other two rules do — "centred on the
// window", "offset from the fireplace" — because there is no way to know which
// element is meant except by pointing at it.
//
// PURE: the state a host holds while a pick is in flight, the rules about what
// may be picked, and the record a completed pick produces. No React, no
// hit-testing — the host resolves a click to a ref and hands it here.
//
// See claude-docs/2026-09-15-unified-dimensioning.md §6.

const { normalizeRef, refKey } = require('../group-customization/refs');
const { DIMENSION_RULES } = require('./rules');
const { dimensionRecordRefusal } = require('./records');

// The rules whose datum must be pointed at. `centered-between` is absent on
// purpose — offering a pick for something already auto-picked would be a
// worse control, not a more powerful one.
const PICKED_RULES = new Set(['centered-on', 'offset-from']);

const PROMPTS = {
  'centered-on': 'Click the element to centre on',
  'offset-from': 'Click the element to measure from',
};

const safeKey = (ref) => {
  try {
    return refKey(normalizeRef(ref));
  } catch {
    return null;
  }
};

/**
 * Start a pick, or null when there is nothing to pick.
 *
 * Two flavours. Without a `slot` it starts a NEW record, and only the rules
 * whose datum must be pointed at can do that. With a slot it REPLACES one
 * datum of a record that already exists — and `centered-between` allows that
 * even though its datums were auto-picked, because the auto-pick is only ever
 * a good guess. A room opening wide to the next room has no wall spanning the
 * subject on that side, so the pair worth centring between is one the guess
 * cannot see.
 *
 * @param {Object|string} subjectRef
 * @param {string} rule
 * A third flavour, `seed`, STARTS a `centered-between` from a pick when the
 * auto-pick could offer nothing — a room open wide to the next one has no
 * facing wall spanning the subject, so there is no record to edit the datums
 * of. It fills the first slot and leaves the second empty for a chip to fill.
 *
 * @param {{slot?: number, recordId?: string, seed?: boolean,
 *   filled?: boolean}} [opts]  `filled` says the slot already holds a datum,
 *   which is the difference between replacing one and completing a pair.
 */
function beginDatumPick(subjectRef, rule, opts = {}) {
  if (!safeKey(subjectRef)) return null;
  if (!DIMENSION_RULES[rule]) return null;
  const replacing = Number.isInteger(opts.slot);
  if (opts.seed) return { subject: subjectRef, rule, seed: true };
  if (!replacing && !PICKED_RULES.has(rule)) return null;
  return replacing
    ? {
        subject: subjectRef,
        rule,
        slot: opts.slot,
        recordId: opts.recordId,
        filled: !!opts.filled,
      }
    : { subject: subjectRef, rule };
}

/** What the host should tell the user to do next. */
const datumPickPrompt = (pick) => {
  if (pick?.seed) return 'Click the first of the two to centre between';
  if (Number.isInteger(pick?.slot)) {
    // "instead" only makes sense when there is something to replace. Filling
    // the empty second slot of a half-stated centring is not a replacement.
    return pick.filled
      ? 'Click the element to measure to instead'
      : 'Click the second of the two to centre between';
  }
  return PROMPTS[pick?.rule] || 'Click an element';
};

/**
 * Why this candidate may not be the datum, or null when it may. PURE.
 *
 * @param {Object} pick       from `beginDatumPick`.
 * @param {Object} candidate  the ref the host resolved the click to.
 * @param {Object} subjectEntity  the SUBJECT as stored (bay refusals).
 */
function datumPickRefusal(pick, candidate, subjectEntity) {
  if (!pick || !DIMENSION_RULES[pick.rule]) return 'nothing is being picked';
  const candidateKey = safeKey(candidate);
  if (!candidateKey) return 'that cannot be measured to';
  // Measuring something from itself is a zero-length statement.
  if (candidateKey === safeKey(pick.subject)) {
    return 'pick something other than this object';
  }
  return dimensionRecordRefusal(
    { subject: pick.subject, rule: pick.rule },
    subjectEntity
  );
}

/**
 * The record a completed pick states, or null when the pick is refused. PURE.
 *
 * No `axis` is stored on a new record: the datum carries its own direction (a
 * placed object's rotation, a wall's run), and a stored copy would be a second
 * source of truth that drifts when the datum moves. Replacing a datum DROPS
 * any axis the auto-pick had stored, for the same reason — the new datum is
 * now what says which way the dimension runs.
 *
 * @param {Object} [existing]  the record being edited, when replacing a slot.
 */
function recordFromPick(pick, candidate, subjectEntity, existing) {
  if (datumPickRefusal(pick, candidate, subjectEntity)) return null;
  const datum = normalizeRef(candidate);

  if (pick.seed) {
    // The second slot stays empty until a chip fills it. The record draws
    // nothing meanwhile, which is honest: half a statement measures nothing.
    return {
      subject: pick.subject,
      rule: pick.rule,
      datum: [datum, null],
      source: 'user',
    };
  }

  if (Number.isInteger(pick.slot)) {
    if (!existing) return null;
    const current = Array.isArray(existing.datum)
      ? [...existing.datum]
      : [existing.datum];
    if (pick.slot < 0 || pick.slot >= current.length) return null;
    current[pick.slot] = datum;
    const { axis, id, ...rest } = existing;
    return {
      ...rest,
      datum: Array.isArray(existing.datum) ? current : current[0],
      source: 'user',
    };
  }

  return {
    subject: pick.subject,
    rule: pick.rule,
    datum,
    source: 'user',
  };
}

module.exports = {
  PICKED_RULES,
  beginDatumPick,
  datumPickPrompt,
  datumPickRefusal,
  recordFromPick,
};
